import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

export default function GestaoContratosPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Gestão de Contratos de Seguros",
    "provider": {
      "@type": "Organization",
      "name": "IBX Brasil"
    },
    "description": "Gestão completa de contratos de seguros empresariais com controle de vencimentos, renovações, análise de coberturas e otimização de custos.",
    "serviceType": "Gestão de Contratos de Seguros",
    "areaServed": "BR"
  };

  return (
    <>
      <SEOHead
        title="Gestão de Contratos de Seguros | Controle e Otimização"
        description="Gestão completa de contratos de seguros empresariais. Controle de vencimentos, renovações automáticas, análise de coberturas e otimização de custos para sua empresa."
        canonical="https://ibxbrasil.com.br/servicos/gestao-contratos"
        ogImage="https://readdy.ai/api/search-image?query=professional%20business%20consultant%20managing%20insurance%20contracts%20and%20documents%20organized%20desk%20with%20folders%20laptop%20calendar%20contract%20papers%20modern%20office%20clean%20background%20corporate%20professional%20photography&width=1200&height=630&seq=seo-gestao-contratos-service&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="SERVIÇOS • GESTÃO DE CONTRATOS"
          title="Gestão Profissional de Contratos de Seguros"
          highlightedWord="Contratos"
          highlightColor="orange"
          subtitle="Administração completa de apólices, renovações estratégicas e otimização contínua de coberturas para empresas que levam gestão de riscos a sério."
          benefits={[
            'Gestão centralizada de todas as apólices',
            'Renovações estratégicas com economia',
            'Monitoramento contínuo de coberturas',
            'Relatórios gerenciais detalhados'
          ]}
          ctaText="Falar no WhatsApp"
          ctaLink="https://wa.me/5577988080505?text=Olá! Gostaria de saber mais sobre gestão de contratos de seguros."
          ctaSecondary={{
            text: 'Solicitar Análise',
            link: '#contato'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20insurance%20contract%20management%20office%20with%20organized%20documents%20digital%20systems%20modern%20corporate%20environment%20business%20efficiency%20strategic%20planning%20atmosphere&width=1920&height=700&seq=gestao-contratos-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* What We Manage */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                O Que Gerenciamos
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Controle completo de todos os aspectos dos seus contratos de seguros
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calendar-check-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Vigências e Renovações</h3>
                <p className="text-gray-600 leading-relaxed">
                  Controle de prazos, alertas de vencimento e preparação antecipada para renovações estratégicas.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Apólices e Coberturas</h3>
                <p className="text-gray-600 leading-relaxed">
                  Revisão técnica periódica de coberturas contratadas e adequação às mudanças de riscos.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Custos e Pagamentos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Monitoramento de prêmios, análise de reajustes e identificação de oportunidades de economia.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-alert-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Sinistros e Acionamentos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Registro de ocorrências, acompanhamento de processos e análise de impacto no histórico.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Indicadores de Performance</h3>
                <p className="text-gray-600 leading-relaxed">
                  Relatórios de sinistralidade, economia gerada e efetividade das coberturas contratadas.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Conformidade Contratual</h3>
                <p className="text-gray-600 leading-relaxed">
                  Verificação de cumprimento de cláusulas e condições estabelecidas pelas seguradoras.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Ciclo de Gestão Contínua
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Acompanhamento estruturado durante toda a vigência dos contratos
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Cadastro e Organização</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Registro completo de todas as apólices, coberturas, vigências e condições contratuais em sistema de controle.
                </p>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Monitoramento Ativo</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Acompanhamento mensal de vigências, pagamentos, alterações de riscos e oportunidades de otimização.
                </p>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Revisão Periódica</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Análise trimestral de adequação das coberturas, custos e condições contratuais frente às mudanças de cenário.
                </p>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Renovação Estratégica</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Renegociação com 90 dias de antecedência para garantir melhores condições e evitar lapsos de cobertura.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Resultados da Gestão Profissional
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Benefícios mensuráveis para sua empresa
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-percent-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Economia Recorrente</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Redução média de 15% a 25% nos custos anuais através de renegociações estratégicas e otimização de coberturas.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ganho de Tempo</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Eliminação de tarefas operacionais de controle, permitindo foco total na gestão do negócio.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Proteção Garantida</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Coberturas sempre atualizadas e adequadas aos riscos reais, sem lapsos ou falhas de proteção.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-bar-chart-box-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Visibilidade Total</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Relatórios gerenciais com indicadores de custos, sinistralidade e oportunidades de melhoria.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Included */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Serviços Incluídos na Gestão
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Acompanhamento completo e especializado
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Revisão Técnica de Apólices</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Análise detalhada de todas as cláusulas contratuais, coberturas, exclusões e condições especiais para garantir conformidade e adequação.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Verificação de coberturas contratadas vs. necessidades reais</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Identificação de cláusulas restritivas ou prejudiciais</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Análise de franquias e participações obrigatórias</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-refresh-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Renegociação Anual</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Processo estruturado de renovação com análise de mercado, cotação com múltiplas seguradoras e negociação de condições.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Início do processo 90 dias antes do vencimento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Comparação técnica de propostas de múltiplas seguradoras</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Negociação de melhores condições e tarifas</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-chart-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Relatórios Gerenciais</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Documentação completa com indicadores financeiros, análise de sinistralidade e recomendações estratégicas.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Relatório mensal de vigências e pagamentos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Análise trimestral de custos e economia gerada</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Relatório anual consolidado com recomendações</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Suporte Contínuo</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Atendimento prioritário para dúvidas, alterações contratuais e suporte em situações de sinistro.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Canal direto de atendimento via WhatsApp</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Orientação técnica em processos de sinistro</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                        <span className="text-gray-600 text-sm">Suporte para alterações e endossos</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Otimize Sua Gestão de Seguros Agora
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Solicite uma análise gratuita e descubra como nossa gestão pode gerar economia e segurança
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Análise
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}